import { noop } from 'lodash';
import { createContext, memo, type ReactNode, useContext, useEffect, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import {
  type PrivateWorkspaceAgentAssetListItems,
  usePrivateWorkspaceAgentAssetListItems,
} from '~/components/v2/components/workspace/workspace-agent-asset-list/hooks/usePrivateWorkspaceAgentAssetListItems';
import { usePrivateWorkspaceAgentSessionArtifactsList } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionArtifactsList';
import { usePrivateWorkspaceAgentSessionGet } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionGet';
import { useWorkspaceAgentAssetListViewStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentAssetListViewStore';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PrivateWorkspaceAgentAssetListContextValue = PrivateWorkspaceAgentAssetListItems & {
  artifactCreatedAt: string | undefined;
  artifactId: string | undefined;
  sessionId: string | undefined;
  /** The agent session's title; the asset list artifact carries none of its own. */
  sessionTitle: string | undefined;
  workspaceId: string | undefined;
};

const defaultContextValue: PrivateWorkspaceAgentAssetListContextValue = {
  artifactCreatedAt: undefined,
  artifactId: undefined,
  galleryItemsData: {
    data: {
      boards: { hasMore: false, isLoading: false, items: [], total: 0 },
      clips: { hasMore: false, isLoading: false, items: [], total: 0 },
      files: { hasMore: false, isLoading: false, items: [], total: 0 },
    },
    isEmpty: true,
    isLoading: false,
    loadNextPage: noop,
  },
  hasMore: false,
  isError: false,
  isLoading: false,
  failedCount: 0,
  isWrongType: false,
  sessionId: undefined,
  tableItemsData: {
    data: [],
    hasMore: false,
    isEmpty: true,
    isLoading: false,
    isLoadingMore: false,
    loadNextPage: noop,
    total: 0,
  },
  sessionTitle: undefined,
  totalCount: 0,
  unavailableCount: 0,
  workspaceId: undefined,
};

const PrivateWorkspaceAgentAssetListContext =
  createContext<PrivateWorkspaceAgentAssetListContextValue>(defaultContextValue);

export const usePrivateWorkspaceAgentAssetListContext = () => {
  const context = useContext(PrivateWorkspaceAgentAssetListContext);

  if (context === defaultContextValue) {
    const error = new Error(
      'usePrivateWorkspaceAgentAssetListContext must be used within PrivateWorkspaceAgentAssetListProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ context: 'Workspace agent asset list context not found', error });
  }

  return context;
};

export type PrivateWorkspaceAgentAssetListProviderProps = {
  artifactId: string | undefined;
  children: ReactNode;
  sessionId: string | undefined;
  workspaceId: string | undefined;
};

export const PrivateWorkspaceAgentAssetListProvider = memo(
  ({ artifactId, children, sessionId, workspaceId }: PrivateWorkspaceAgentAssetListProviderProps) => {
    const items = usePrivateWorkspaceAgentAssetListItems({ artifactId, sessionId, workspaceId });
    const { data: session } = usePrivateWorkspaceAgentSessionGet({ sessionId, workspaceId });
    const artifactIds = useMemo(() => (artifactId ? [artifactId] : []), [artifactId]);
    const { data: artifactsData } = usePrivateWorkspaceAgentSessionArtifactsList({
      artifactIds,
      sessionId,
      workspaceId,
    });
    const artifact = artifactsData?.data.find((entry) => entry.id === artifactId);
    const sessionTitle = session?.title || undefined;
    const openArtifactTab = useWorkspaceAgentAssetListViewStore((state) => state.openArtifactTab);

    useEffect(() => {
      if (artifactId && sessionId) {
        openArtifactTab({ artifactId, sessionId });
      }
    }, [artifactId, openArtifactTab, sessionId]);

    const value = useMemo(
      () => ({
        ...items,
        artifactCreatedAt: artifact?.createdAt,
        artifactId,
        sessionId,
        sessionTitle,
        workspaceId,
      }),
      [artifact?.createdAt, artifactId, items, sessionId, sessionTitle, workspaceId],
    );

    return (
      <PrivateWorkspaceAgentAssetListContext.Provider value={value}>
        {children}
      </PrivateWorkspaceAgentAssetListContext.Provider>
    );
  },
);

PrivateWorkspaceAgentAssetListProvider.displayName = 'PrivateWorkspaceAgentAssetListProvider';
